// ═══════════════════════════════════════════════════════════════
// DONOR RECOGNITION REPORT
// Path: src/components/reports/DonorRecognitionReport.jsx
//
// Two sub-views in one feature:
//   1. Recognition List — wall-style output grouped by tier,
//      alphabetical within each tier. Suitable for printing on
//      programs and the donor wall. Skips anonymous donors.
//   2. Review & Edit   — flat editable table for cleaning up
//      publication names and anonymity flags. Includes filters
//      and a one-click "Seed blank names" button so the bulk
//      population can happen inside the app instead of via SQL.
//
// Tier sources:
//   - Perpetuity tiers resolve against members.lifetime_giving
//     (cached column maintained by the gifts trigger on PCPA).
//   - Annual tiers resolve against the sum of gift.amount inside
//     the chosen date range.
//
// Quick ranges include the current and prior fiscal year so the
// twice-a-year run is one click. Custom range supported for
// season-style windows.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Award,
  Printer,
  Filter,
  Search,
  Sparkles,
  Pencil,
  RotateCcw,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { TabBar } from "../ui/TabBar";
import { resolveGivingLevel } from "../../constants/giftTypes";
import { supabase } from "../../lib/supabase";
import { getFiscalDates } from "../../utils/fiscalYear";

// ── Helpers ──────────────────────────────────────────────────────

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

// Reproduces the SQL seed logic in JS so we can run it from the UI.
// Returns the suggested publication name for a member, never null.
const seedNameFromMember = (m) => {
  if (!m) return "";
  const first = (m.firstName || "").trim();
  const last = (m.lastName || "").trim();
  const spouseFirst = (m.spouseFirstName || "").trim();
  const householdName = (m.householdName || "").trim();
  const organization = (m.organization || "").trim();

  if (m.donorType === "household") {
    if (householdName) return householdName;
    const parts = [];
    if (first) parts.push(first);
    if (spouseFirst) parts.push(`& ${spouseFirst}`);
    if (last) parts.push(last);
    return parts.join(" ").trim();
  }

  if (m.donorType === "organization") {
    return organization || `${first} ${last}`.trim();
  }

  // individual or null
  return `${first} ${last}`.trim();
};

// Returns the *display* publication name — manual value if set,
// otherwise the seeded fallback. Used by the Recognition List view.
const resolveDisplayName = (m) => {
  const explicit = (m.publicationName || "").trim();
  return explicit || seedNameFromMember(m);
};

// Donor-wall alphabetical sort key. Uses the explicit last_name when
// present (so "Mr. and Mrs. John Smith" still sorts under S), and
// falls back to the display name with a leading "The " stripped for
// households or orgs that don't carry a last_name.
const lastNameSortKey = (row) => {
  const last = (row.lastName || "").trim().toLowerCase();
  if (last) return last;
  return (row.displayName || "")
    .trim()
    .toLowerCase()
    .replace(/^the\s+/, "");
};

// Minimal HTML escape for the .xls export.
const escapeHtml = (str) =>
  String(str ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
  );

// Trigger a file download from a string blob.
const triggerDownload = (content, filename, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ── Main Component ───────────────────────────────────────────────

export const DonorRecognitionReport = () => {
  const {
    palette,
    members,
    chapterSettings,
    updateMember,
    showMessage,
    navigate,
    dataVersion,
  } = useApp();

  const givingLevels = chapterSettings?.givingLevels || [];
  const fiscalStart = chapterSettings?.fiscalYearStartMonth || 1;
  const fiscal = getFiscalDates(fiscalStart);

  // Sub-tab inside the report. Default to Review since that's the
  // urgent workflow — once names are clean, Glen flips to List.
  const [subTab, setSubTab] = useState("review");

  // Annual window controls (drives the annual-tier computation)
  const [dateFrom, setDateFrom] = useState(fiscal.yearStart);
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));

  // Review tab state
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [seeding, setSeeding] = useState(false);

  // Loaded gifts for the annual window
  const [gifts, setGifts] = useState([]);
  const [loadingGifts, setLoadingGifts] = useState(true);

  // Quick ranges
  const setRange = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
  };

  const quickRanges = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const year = now.getFullYear();

    const prevFyStartYear = parseInt(fiscal.yearStart.slice(0, 4)) - 1;
    const prevFyStart = `${prevFyStartYear}-${fiscal.yearStart.slice(5)}`;
    const prevFyEnd = new Date(
      new Date(fiscal.yearStart + "T12:00:00").getTime() - 86400000
    );
    const prevFyEndStr = prevFyEnd.toISOString().slice(0, 10);
    const prevFyLabel =
      fiscalStart === 1
        ? `CY ${prevFyStartYear}`
        : `FY ${prevFyStartYear}/${String(prevFyStartYear + 1).slice(2)}`;

    return [
      { label: fiscal.label, from: fiscal.yearStart, to: todayStr },
      { label: prevFyLabel, from: prevFyStart, to: prevFyEndStr },
      { label: `CY ${year}`, from: `${year}-01-01`, to: todayStr },
      { label: `CY ${year - 1}`, from: `${year - 1}-01-01`, to: `${year - 1}-12-31` },
    ];
  }, [fiscal, fiscalStart]);

  // ── Load gifts for the annual window ────────────────────────────
  const loadGifts = useCallback(async () => {
    setLoadingGifts(true);
    try {
      let all = [];
      let lastId = null;
      const pageSize = 1000;
      while (true) {
        let q = supabase
          .from("gifts")
          .select("id, amount, gift_date, member_id")
          .gte("gift_date", dateFrom)
          .lte("gift_date", dateTo)
          .order("id", { ascending: true })
          .limit(pageSize);
        if (lastId) q = q.gt("id", lastId);
        const { data, error } = await q;
        if (error) throw error;
        if (!data || data.length === 0) break;
        all = all.concat(data);
        lastId = data[data.length - 1].id;
        if (data.length < pageSize) break;
      }
      setGifts(all);
    } catch (err) {
      console.error("DonorRecognitionReport load error:", err);
      showMessage("Failed to load gifts for the selected range", "error");
    } finally {
      setLoadingGifts(false);
    }
  }, [dateFrom, dateTo, dataVersion, showMessage]);

  useEffect(() => {
    loadGifts();
  }, [loadGifts]);

  // ── Build per-donor rows enriched with tier resolutions ────────
  const rows = useMemo(() => {
    // Sum gifts in the chosen window per member
    const annualByMember = {};
    gifts.forEach((g) => {
      const mid = g.member_id;
      if (!mid) return;
      annualByMember[mid] =
        (annualByMember[mid] || 0) + (parseFloat(g.amount) || 0);
    });

    return members.map((m) => {
      const lifetime =
        typeof m.lifetimeGiving === "number"
          ? m.lifetimeGiving
          : parseFloat(m.lifetimeGiving) || 0;
      const annual = annualByMember[m.id] || 0;

      // Manual override (set on the donor detail form) wins over
      // the amount-driven tier. Place the donor under whichever
      // scope (perpetuity or annual) the matched tier belongs to,
      // so a donor pinned to an annual tier appears only on the
      // annual list and vice versa.
      const overrideName = (m.givingLevelOverride || "").trim() || null;
      const overrideTier = overrideName
        ? givingLevels.find((t) => t.name === overrideName)
        : null;
      const overrideScope = overrideTier
        ? overrideTier.scope || "perpetuity"
        : null;

      const perpTier =
        overrideScope === "perpetuity"
          ? overrideName
          : resolveGivingLevel(lifetime, givingLevels, "perpetuity");
      const annualTier =
        overrideScope === "annual"
          ? overrideName
          : resolveGivingLevel(annual, givingLevels, "annual");
      const displayName = resolveDisplayName(m);
      const seedSuggestion = seedNameFromMember(m);
      const pubBlank = !(m.publicationName || "").trim();
      return {
        id: m.id,
        member: m,
        firstName: m.firstName || "",
        lastName: m.lastName || "",
        organization: m.organization || "",
        donorType: m.donorType || "individual",
        publicationName: m.publicationName || "",
        seedSuggestion,
        displayName,
        pubBlank,
        isAnonymous: !!m.isAnonymous,
        lifetime,
        annual,
        perpTier,
        annualTier,
        qualifies: !!perpTier || !!annualTier,
      };
    });
  }, [members, gifts, givingLevels]);

  // ── Tier metadata (sorted high → low for display) ──────────────
  const perpTierDefs = useMemo(
    () =>
      [...givingLevels]
        .filter((t) => (t.scope || "perpetuity") === "perpetuity")
        .sort((a, b) => (b.min_amount || 0) - (a.min_amount || 0)),
    [givingLevels]
  );
  const annualTierDefs = useMemo(
    () =>
      [...givingLevels]
        .filter((t) => t.scope === "annual")
        .sort((a, b) => (b.min_amount || 0) - (a.min_amount || 0)),
    [givingLevels]
  );

  // ── Group rows by tier for the Recognition List ─────────────────
  const listSections = useMemo(() => {
    const visible = rows.filter((r) => !r.isAnonymous && r.qualifies);

    const buildGroup = (tierDefs, key) =>
      tierDefs.map((tier) => {
        const donors = visible
          .filter((r) => r[key] === tier.name)
          .sort((a, b) => lastNameSortKey(a).localeCompare(lastNameSortKey(b)));
        return { tier, donors };
      });

    return {
      perpetuity: buildGroup(perpTierDefs, "perpTier"),
      annual: buildGroup(annualTierDefs, "annualTier"),
    };
  }, [rows, perpTierDefs, annualTierDefs]);

  // Flat rows for CSV / Excel — preserves the same section order
  // and per-tier alphabetical sort that the on-screen list uses.
  const exportRows = useMemo(() => {
    const out = [];
    const push = (section, tier, d) =>
      out.push({
        Section: section,
        Tier: tier.name,
        "Publication Name": d.displayName,
        "Last Name": d.lastName || "",
        "First Name": d.firstName || "",
        Organization: d.organization || "",
        "Donor Type": d.donorType || "",
        "Lifetime Giving": Number(d.lifetime || 0).toFixed(2),
        "Annual Giving": Number(d.annual || 0).toFixed(2),
      });
    listSections.perpetuity.forEach(({ tier, donors }) =>
      donors.forEach((d) => push("Perpetuity", tier, d))
    );
    listSections.annual.forEach(({ tier, donors }) =>
      donors.forEach((d) => push("Annual", tier, d))
    );
    return out;
  }, [listSections]);

  // ── Filter rows for the Review tab ──────────────────────────────
  const reviewRows = useMemo(() => {
    let r = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      r = r.filter((row) =>
        [
          row.firstName,
          row.lastName,
          row.publicationName,
          row.organization,
          row.member.email || "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    if (filter === "blank") r = r.filter((row) => row.pubBlank);
    if (filter === "anonymous") r = r.filter((row) => row.isAnonymous);
    if (filter === "qualifying") r = r.filter((row) => row.qualifies);
    if (filter === "duplicates") {
      const counts = {};
      rows.forEach((row) => {
        const key = (row.lastName || "").trim().toLowerCase();
        if (!key) return;
        counts[key] = (counts[key] || 0) + 1;
      });
      r = r.filter(
        (row) =>
          row.lastName &&
          counts[row.lastName.trim().toLowerCase()] >= 2 &&
          row.qualifies
      );
    }
    return r.sort((a, b) =>
      (a.lastName || "").localeCompare(b.lastName || "")
    );
  }, [rows, search, filter]);

  const blankCount = useMemo(
    () => rows.filter((r) => r.pubBlank && !!r.seedSuggestion).length,
    [rows]
  );

  // ── Edit handlers ────────────────────────────────────────────────

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditingValue(row.publicationName || "");
  };

  const commitEdit = async (row) => {
    const next = (editingValue || "").trim();
    setEditingId(null);
    if (next === (row.publicationName || "")) return;
    try {
      await updateMember(row.id, { publicationName: next }, true);
    } catch (err) {
      showMessage("Save failed: " + err.message, "error");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleToggleAnonymous = async (row) => {
    try {
      await updateMember(
        row.id,
        { isAnonymous: !row.isAnonymous },
        true
      );
    } catch (err) {
      showMessage("Save failed: " + err.message, "error");
    }
  };

  const handleSeedSingle = async (row) => {
    const next = row.seedSuggestion;
    if (!next) {
      showMessage("No source name available to seed from", "warning");
      return;
    }
    try {
      await updateMember(row.id, { publicationName: next }, true);
    } catch (err) {
      showMessage("Save failed: " + err.message, "error");
    }
  };

  const handleSeedAllBlanks = async () => {
    const targets = rows.filter((r) => r.pubBlank && !!r.seedSuggestion);
    if (targets.length === 0) {
      showMessage("No blank publication names to seed", "info");
      return;
    }
    if (
      !window.confirm(
        `Seed ${targets.length} blank publication name${
          targets.length === 1 ? "" : "s"
        } using existing First/Last/Household/Organization fields? Records you have already filled in will be left alone.`
      )
    ) {
      return;
    }
    setSeeding(true);
    let ok = 0;
    let fail = 0;
    for (const r of targets) {
      try {
        await updateMember(
          r.id,
          { publicationName: r.seedSuggestion },
          true
        );
        ok++;
      } catch (err) {
        fail++;
        console.error("seed failed for", r.id, err);
      }
    }
    setSeeding(false);
    showMessage(
      `Seeded ${ok} record${ok === 1 ? "" : "s"}${
        fail ? ` (${fail} failed)` : ""
      }`,
      fail ? "warning" : "success"
    );
  };

  const handlePrint = () => {
    document.body.classList.add("recognition-print-active");
    window.requestAnimationFrame(() => {
      window.print();
      window.setTimeout(() => {
        document.body.classList.remove("recognition-print-active");
      }, 500);
    });
  };

  const handleExportCsv = () => {
    if (exportRows.length === 0) {
      showMessage("No donors qualify — nothing to export", "warning");
      return;
    }
    const headers = Object.keys(exportRows[0]);
    const csvRows = exportRows.map((row) =>
      headers
        .map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...csvRows].join("\n");
    triggerDownload(
      csv,
      `donor_recognition_${dateFrom}_to_${dateTo}.csv`,
      "text/csv;charset=utf-8;"
    );
  };

  const handleExportExcel = () => {
    if (exportRows.length === 0) {
      showMessage("No donors qualify — nothing to export", "warning");
      return;
    }
    const headers = Object.keys(exportRows[0]);
    const headerHtml = headers
      .map(
        (h) =>
          `<th style="background:#1f2937;color:#fff;padding:6px 10px;text-align:left;border:1px solid #d1d5db;">${escapeHtml(
            h
          )}</th>`
      )
      .join("");
    const bodyHtml = exportRows
      .map((row, i) => {
        const bg = i % 2 === 0 ? "#ffffff" : "#f9fafb";
        const cells = headers
          .map((h) => {
            const isMoney =
              h === "Lifetime Giving" || h === "Annual Giving";
            const align = isMoney ? "right" : "left";
            return `<td style="padding:5px 10px;border:1px solid #e5e7eb;background:${bg};text-align:${align};">${escapeHtml(
              row[h]
            )}</td>`;
          })
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Donor Recognition</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:11pt;"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
    triggerDownload(
      html,
      `donor_recognition_${dateFrom}_to_${dateTo}.xls`,
      "application/vnd.ms-excel"
    );
  };

  // ── Empty / config-missing states ────────────────────────────────

  if (givingLevels.length === 0) {
    return (
      <Card style={{ padding: 24, textAlign: "center" }}>
        <Award
          size={ICON_SIZES.xxl}
          style={{ color: palette.text3, marginBottom: 12 }}
        />
        <div
          style={{
            fontSize: FONT_SIZES.md,
            fontWeight: 700,
            color: palette.text1,
            fontFamily: FONT_FAMILY,
            marginBottom: 8,
          }}
        >
          No Giving Levels Configured
        </div>
        <p
          style={{
            color: palette.text2,
            fontSize: FONT_SIZES.sm,
            lineHeight: 1.5,
            margin: "0 auto",
            maxWidth: 460,
          }}
        >
          Add your perpetuity and annual recognition tiers in{" "}
          <strong>Settings → Giving Levels</strong>, then return here to build
          your donor wall list.
        </p>
        <div style={{ marginTop: 16 }}>
          <ActionButton
            label="Open Settings"
            icon={Award}
            onClick={() => navigate("settings")}
          />
        </div>
      </Card>
    );
  }

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="recognition-report-root">
      {/* Inline print stylesheet — hides the chrome and renders only
          the Recognition List when window.print() fires. */}
      <style>{`
        @media print {
          body.recognition-print-active * {
            visibility: hidden !important;
          }
          body.recognition-print-active .recognition-print-area,
          body.recognition-print-active .recognition-print-area * {
            visibility: visible !important;
          }
          body.recognition-print-active .recognition-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          body.recognition-print-active .recognition-no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* ── Annual Window Controls ──────────────────────────── */}
      <Card
        className="recognition-no-print"
        style={{ marginBottom: 14, padding: 14 }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Filter size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
          <span
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: 700,
              color: palette.text2,
              fontFamily: FONT_FAMILY,
            }}
          >
            Annual Window:
          </span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={inputStyle(palette)}
          />
          <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3 }}>
            to
          </span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={inputStyle(palette)}
          />
          <div
            style={{
              width: 1,
              height: 20,
              background: palette.cardBorder,
              margin: "0 4px",
            }}
          />
          {quickRanges.map((qr) => (
            <button
              key={qr.label}
              onClick={() => setRange(qr.from, qr.to)}
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                fontSize: "0.65rem",
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
                border: "none",
                background:
                  dateFrom === qr.from && dateTo === qr.to
                    ? palette.accent1
                    : `${palette.accent1}10`,
                color:
                  dateFrom === qr.from && dateTo === qr.to
                    ? "#fff"
                    : palette.accent1,
                transition: "all 0.15s",
              }}
            >
              {qr.label}
            </button>
          ))}
          <span
            style={{
              fontSize: 11,
              color: palette.text3,
              marginLeft: "auto",
              fontFamily: FONT_FAMILY,
            }}
          >
            Perpetuity tiers always use lifetime giving — the window above
            only affects annual tiers.
          </span>
        </div>
      </Card>

      {/* ── Sub-tab toggle ────────────────────────────────────── */}
      <div className="recognition-no-print">
        <TabBar
          tabs={[
            { id: "review", label: "Review & Edit", icon: Pencil },
            { id: "list", label: "Recognition List", icon: Award },
          ]}
          activeTab={subTab}
          onTabChange={setSubTab}
        />
      </div>

      {/* ═══ REVIEW & EDIT ═══ */}
      {subTab === "review" && (
        <div className="recognition-no-print" style={{ marginTop: 14 }}>
          {/* Toolbar */}
          <Card style={{ marginBottom: 14, padding: 14 }}>
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flex: "1 1 240px",
                }}
              >
                <Search size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, organization, email..."
                  style={{ ...inputStyle(palette), flex: 1 }}
                />
              </div>

              {[
                { id: "all", label: "All" },
                { id: "qualifying", label: "Tier-eligible" },
                { id: "blank", label: "Blank Names" },
                { id: "anonymous", label: "Anonymous" },
                { id: "duplicates", label: "Shared Last Name" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 6,
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                    cursor: "pointer",
                    border: "none",
                    background:
                      filter === f.id
                        ? palette.accent1
                        : `${palette.accent1}10`,
                    color: filter === f.id ? "#fff" : palette.accent1,
                  }}
                >
                  {f.label}
                </button>
              ))}

              <div
                style={{
                  width: 1,
                  height: 20,
                  background: palette.cardBorder,
                  margin: "0 4px",
                }}
              />

              <ActionButton
                label={
                  seeding
                    ? "Seeding..."
                    : `Seed ${blankCount} Blank Name${blankCount === 1 ? "" : "s"}`
                }
                icon={Sparkles}
                small
                onClick={handleSeedAllBlanks}
                disabled={seeding || blankCount === 0}
              />
            </div>
            <div
              style={{
                marginTop: 10,
                fontSize: 11,
                color: palette.text3,
                fontFamily: FONT_FAMILY,
                lineHeight: 1.5,
              }}
            >
              Click a publication name cell to edit. Use{" "}
              <strong>Shared Last Name</strong> to find couples that exist as
              two individual records — for each pair, set one record's
              publication name to "Mr. and Mrs..." and toggle the other to{" "}
              <strong>Anonymous</strong> to prevent double-listing.
            </div>
          </Card>

          {/* Table */}
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ maxHeight: 600, overflow: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontFamily: FONT_FAMILY,
                  fontSize: FONT_SIZES.xs,
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyle(palette)}>Publication Name</th>
                    <th style={thStyle(palette)}>Source Record</th>
                    <th style={thStyle(palette)}>Type</th>
                    <th style={{ ...thStyle(palette), textAlign: "right" }}>
                      Lifetime
                    </th>
                    <th style={thStyle(palette)}>Perp Tier</th>
                    <th style={{ ...thStyle(palette), textAlign: "right" }}>
                      Annual
                    </th>
                    <th style={thStyle(palette)}>Annual Tier</th>
                    <th
                      style={{ ...thStyle(palette), textAlign: "center" }}
                      title="Anonymous — suppress from donor wall"
                    >
                      Anon
                    </th>
                    <th style={{ ...thStyle(palette), width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {reviewRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          padding: 32,
                          textAlign: "center",
                          color: palette.text3,
                        }}
                      >
                        No donors match the current filter.
                      </td>
                    </tr>
                  ) : (
                    reviewRows.map((r) => {
                      const isEditing = editingId === r.id;
                      return (
                        <tr
                          key={r.id}
                          style={{
                            background: r.isAnonymous
                              ? "#fef3c7"
                              : "transparent",
                          }}
                        >
                          {/* Publication Name */}
                          <td style={tdStyle(palette)}>
                            {isEditing ? (
                              <input
                                autoFocus
                                value={editingValue}
                                onChange={(e) =>
                                  setEditingValue(e.target.value)
                                }
                                onBlur={() => commitEdit(r)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") commitEdit(r);
                                  if (e.key === "Escape") cancelEdit();
                                }}
                                style={{
                                  ...inputStyle(palette),
                                  width: "100%",
                                  fontWeight: 600,
                                }}
                              />
                            ) : (
                              <button
                                onClick={() => startEdit(r)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: 0,
                                  textAlign: "left",
                                  fontFamily: FONT_FAMILY,
                                  fontSize: FONT_SIZES.xs,
                                  color: r.pubBlank
                                    ? palette.text3
                                    : palette.text1,
                                  fontWeight: r.pubBlank ? 400 : 600,
                                  fontStyle: r.pubBlank ? "italic" : "normal",
                                  width: "100%",
                                }}
                                title="Click to edit"
                              >
                                {r.pubBlank
                                  ? `(blank — would seed: ${
                                      r.seedSuggestion || "—"
                                    })`
                                  : r.publicationName}
                              </button>
                            )}
                          </td>

                          {/* Source record */}
                          <td
                            style={{
                              ...tdStyle(palette),
                              color: palette.text2,
                            }}
                          >
                            <button
                              onClick={() =>
                                navigate("memberDetail", { id: r.id })
                              }
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 0,
                                color: palette.accent1,
                                fontFamily: FONT_FAMILY,
                                fontSize: FONT_SIZES.xs,
                              }}
                              title="Open donor record"
                            >
                              {r.lastName ? `${r.lastName}, ` : ""}
                              {r.firstName || ""}
                              {r.organization ? ` · ${r.organization}` : ""}
                            </button>
                          </td>

                          {/* Type */}
                          <td
                            style={{
                              ...tdStyle(palette),
                              color: palette.text3,
                              textTransform: "capitalize",
                            }}
                          >
                            {r.donorType}
                          </td>

                          {/* Lifetime */}
                          <td
                            style={{
                              ...tdStyle(palette),
                              textAlign: "right",
                              color: palette.text1,
                              fontWeight: 600,
                            }}
                          >
                            {fmtCurrency(r.lifetime)}
                          </td>

                          {/* Perp tier */}
                          <td style={tdStyle(palette)}>
                            {r.perpTier ? (
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: 4,
                                  background: `${palette.accent1}18`,
                                  color: palette.accent1,
                                  fontWeight: 700,
                                  fontSize: 10,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                {r.perpTier}
                              </span>
                            ) : (
                              <span style={{ color: palette.text3 }}>—</span>
                            )}
                          </td>

                          {/* Annual */}
                          <td
                            style={{
                              ...tdStyle(palette),
                              textAlign: "right",
                              color: palette.text2,
                            }}
                          >
                            {fmtCurrency(r.annual)}
                          </td>

                          {/* Annual tier */}
                          <td style={tdStyle(palette)}>
                            {r.annualTier ? (
                              <span
                                style={{
                                  display: "inline-block",
                                  padding: "2px 8px",
                                  borderRadius: 4,
                                  background: "#dbeafe",
                                  color: "#1e3a8a",
                                  fontWeight: 700,
                                  fontSize: 10,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                {r.annualTier}
                              </span>
                            ) : (
                              <span style={{ color: palette.text3 }}>—</span>
                            )}
                          </td>

                          {/* Anonymous toggle */}
                          <td
                            style={{
                              ...tdStyle(palette),
                              textAlign: "center",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={r.isAnonymous}
                              onChange={() => handleToggleAnonymous(r)}
                              style={{
                                cursor: "pointer",
                                accentColor: "#d97706",
                              }}
                              title="Anonymous — suppress from donor wall"
                            />
                          </td>

                          {/* Re-seed action */}
                          <td
                            style={{
                              ...tdStyle(palette),
                              textAlign: "right",
                            }}
                          >
                            <button
                              onClick={() => handleSeedSingle(r)}
                              disabled={!r.seedSuggestion}
                              title="Reset to seeded value"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: r.seedSuggestion
                                  ? "pointer"
                                  : "not-allowed",
                                color: palette.text3,
                                padding: 4,
                                opacity: r.seedSuggestion ? 1 : 0.3,
                              }}
                            >
                              <RotateCcw size={ICON_SIZES.xs} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div
              style={{
                padding: "8px 14px",
                borderTop: `1px solid ${palette.cardBorder}`,
                fontSize: 11,
                color: palette.text3,
                fontFamily: FONT_FAMILY,
                background: `${palette.accent1}04`,
              }}
            >
              {reviewRows.length} record{reviewRows.length === 1 ? "" : "s"}{" "}
              shown · {rows.filter((r) => r.qualifies).length} tier-eligible ·{" "}
              {rows.filter((r) => r.isAnonymous).length} anonymous
            </div>
          </Card>
        </div>
      )}

      {/* ═══ RECOGNITION LIST ═══ */}
      {subTab === "list" && (
        <>
          {/* Print header (visible on screen + print) */}
          <div
            className="recognition-no-print"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              margin: "14px 0",
            }}
          >
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                color: palette.text3,
                fontFamily: FONT_FAMILY,
                lineHeight: 1.5,
              }}
            >
              {loadingGifts
                ? "Loading annual giving..."
                : `Annual window: ${dateFrom} → ${dateTo} · ${
                    rows.filter((r) => r.qualifies && !r.isAnonymous).length
                  } donors qualify · ${
                    rows.filter((r) => r.isAnonymous && r.qualifies).length
                  } suppressed (anonymous)`}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <ActionButton
                label="CSV"
                icon={Download}
                onClick={handleExportCsv}
                variant="secondary"
                small
              />
              <ActionButton
                label="Excel"
                icon={FileSpreadsheet}
                onClick={handleExportExcel}
                variant="secondary"
                small
              />
              <ActionButton
                label="Print"
                icon={Printer}
                onClick={handlePrint}
                small
              />
            </div>
          </div>

          {/* Print area */}
          <div
            className="recognition-print-area"
            style={{
              padding: "8px 0",
            }}
          >
            <div
              style={{
                textAlign: "center",
                marginBottom: 24,
                fontFamily: FONT_FAMILY,
              }}
            >
              <div
                style={{
                  fontSize: "1.6rem",
                  fontWeight: 800,
                  color: palette.text1,
                  letterSpacing: "0.02em",
                }}
              >
                Donor Recognition
              </div>
              <div
                style={{
                  fontSize: FONT_SIZES.sm,
                  color: palette.text2,
                  marginTop: 4,
                }}
              >
                Annual window: {dateFrom} – {dateTo}
              </div>
            </div>

            <RecognitionSection
              palette={palette}
              title="Lifetime Recognition (Perpetuity)"
              description="Donors recognized for cumulative lifetime giving."
              groups={listSections.perpetuity}
            />

            <RecognitionSection
              palette={palette}
              title="Annual Giving"
              description={`Donors at these levels for gifts ${dateFrom} – ${dateTo}.`}
              groups={listSections.annual}
            />
          </div>
        </>
      )}
    </div>
  );
};

// ── Recognition Section (one tier set) ───────────────────────────

const RecognitionSection = ({ palette, title, description, groups }) => {
  if (!groups || groups.length === 0) return null;

  const hasAnyDonors = groups.some((g) => g.donors.length > 0);

  return (
    <div style={{ marginBottom: 32, fontFamily: FONT_FAMILY }}>
      <div
        style={{
          borderBottom: `2px solid ${palette.accent1}`,
          paddingBottom: 6,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            fontSize: "1.15rem",
            fontWeight: 800,
            color: palette.text1,
            letterSpacing: "0.02em",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: FONT_SIZES.xs,
            color: palette.text3,
            marginTop: 2,
          }}
        >
          {description}
        </div>
      </div>

      {!hasAnyDonors && (
        <div
          style={{
            padding: "12px 0",
            color: palette.text3,
            fontSize: FONT_SIZES.sm,
            fontStyle: "italic",
          }}
        >
          No qualifying donors in this section.
        </div>
      )}

      {groups.map(({ tier, donors }) => {
        if (donors.length === 0) return null;
        return (
          <div key={tier.id || tier.name} style={{ marginBottom: 22 }}>
            <div
              style={{
                fontSize: FONT_SIZES.md,
                fontWeight: 700,
                color: palette.accent1,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              {tier.name}
            </div>
            <div
              style={{
                fontSize: 11,
                color: palette.text3,
                marginBottom: 8,
              }}
            >
              {tierRangeLabel(tier)}
            </div>
            <div
              style={{
                columnCount: donors.length > 12 ? 3 : donors.length > 6 ? 2 : 1,
                columnGap: 28,
                fontSize: FONT_SIZES.sm,
                color: palette.text1,
                lineHeight: 1.6,
              }}
            >
              {donors.map((d) => (
                <div
                  key={d.id}
                  style={{
                    breakInside: "avoid",
                    pageBreakInside: "avoid",
                  }}
                >
                  {d.displayName || "—"}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Helpers for tier range labels ────────────────────────────────

const tierRangeLabel = (tier) => {
  const min = tier.min_amount || 0;
  const max = tier.max_amount;
  const fmt = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  if (max === null || max === undefined) return `${fmt(min)} and above`;
  return `${fmt(min)} – ${fmt(max)}`;
};

// ── Shared inline styles ─────────────────────────────────────────

const inputStyle = (palette) => ({
  padding: "5px 9px",
  borderRadius: 6,
  fontSize: FONT_SIZES.xs,
  fontFamily: FONT_FAMILY,
  border: `1px solid ${palette.inputBorder}`,
  background: palette.inputBg,
  color: palette.inputColor,
  outline: "none",
});

const thStyle = (palette) => ({
  padding: "8px 10px",
  textAlign: "left",
  fontSize: "0.65rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: palette.text3,
  borderBottom: `2px solid ${palette.cardBorder}`,
  position: "sticky",
  top: 0,
  background: palette.cardBg,
  zIndex: 1,
});

const tdStyle = (palette) => ({
  padding: "5px 10px",
  borderBottom: `1px solid ${palette.cardBorder}`,
  verticalAlign: "middle",
});

